import { MutableRefObject, useEffect, useRef } from 'react';

import { GridApi, gridPaginatedVisibleSortedGridRowIdsSelector } from '@mui/x-data-grid-premium';

const GRID_EVENTS = [
  'rowsSet',
  'sortedRowsSet',
  'filteredRowsSet',
  'paginationModelChange',
] as const;

/**
 * Reports the row ids the grid is actually showing — filtered, sorted, and paginated. Tree
 * expansion is covered without its own subscription: the grid recomputes its filtered rows when
 * a branch expands or collapses, and that fires `filteredRowsSet`. Subscribing to
 * `rowExpansionChange` directly is NOT safe here — it fires mid-pipeline, and the state update
 * this hook feeds would re-enter the grid against half-applied state.
 *
 * Read straight off the grid's own selector rather than re-deriving it: a page that fetches per
 * visible row would otherwise have to re-implement MUI's sort, and get it wrong the moment
 * grouping, tree data or a custom comparator entered the picture.
 *
 * `gridApi` is the mounted instance (see `useMountedGridApi`): a groupBy change or a headers
 * reload remounts the grid, and a subscription bound to the previous instance silently stops
 * reporting.
 */
const useVisibleRowIds = (
  apiRef: MutableRefObject<GridApi>,
  gridApi: GridApi | null,
  onVisibleRowIdsChange: ((rowIds: string[]) => void) | undefined,
) => {
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    if (!onVisibleRowIdsChange) return;

    const report = () => {
      // The selector takes the apiRef itself (it reads `.current.state` and `.current.instanceId`)
      // — handing it `apiRef.current` makes it treat the api object as raw grid state and read
      // straight through an undefined slice. The state guard covers the window before the grid
      // has initialised, which a parent effect can easily run inside; every event below fires
      // after that state exists.
      if (!apiRef.current?.state?.sorting || !apiRef.current.state.pagination) return;
      const rowIds = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef).map(String);
      const key = rowIds.join(',');
      if (key === lastReported.current) return;
      lastReported.current = key;
      onVisibleRowIdsChange(rowIds);
    };

    report();
    if (!gridApi) return undefined;

    const unsubscribes = GRID_EVENTS.map((event) => gridApi.subscribeEvent(event, report));
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe?.());
  }, [apiRef, gridApi, onVisibleRowIdsChange]);
};

export default useVisibleRowIds;
